import {
    callApi,
    errorMessageFromResponse,
    logout,
    NetworkRequestState,
    normaliseDataThunks,
    resetEntities,
} from 'c-data-layer';
import type { Skin, User } from 'c-entity-types';
import apiClient from 'c-data/apiClient';
import { commonSchemas } from 'c-data/schemas';
import type { User as APIUser } from 'c-sdk';
import { UserType } from 'c-sdk';
import type { CommonThunk } from 'c-types';
import * as uiThunks from 'c-wrapper/Slices/UI/ui-thunks';
import type { History } from 'history';
import { PostAuthRoutes } from 'c-routes';
import {
    getStartImpersonatingState,
    getStopImpersonatingState,
} from 'c-auth-module/Slices/Auth/auth-selectors';
import { currentSkin } from 'c-wrapper/Slices/UI/ui-selectors';
import { getPreImpersonateSkin, storePreImpersonateSkin } from 'c-lib';
import { authActions } from './auth-slice';

export const login =
    ({
        email,
        password,
        userType,
        isMobile,
    }: {
        email: string;
        password: string;
        isMobile: boolean;
        userType?: UserType;
    }): CommonThunk =>
    async dispatch => {
        dispatch(authActions.startLogin());
        const [error, data] = await dispatch(
            callApi(apiClient.Auth.login({ email, password, userType }).request),
        );
        if (error || data?.data?.user == null) {
            dispatch(authActions.failedLogin(errorMessageFromResponse(error)));
            return;
        }

        // if a user type has been chosen and set by the API then we can
        if (data.data.credentials?.chosen_user_type != null && data?.data?.user != null) {
            data.data.user.chosenUserType = data.data.credentials.chosen_user_type;

            dispatch(finishLogin(data?.data?.user, isMobile));
        } else if (data?.data?.user?.availableUserTypes?.length > 1) {
            const availableTypes = data.data.user.availableUserTypes;
            // When a user's ONLY two options are Agency and one other type, log them in as
            // the other type automatically instead of prompting them to choose. If there are
            // more than two options, fall through to the normal chooser. The userType == null
            // guard ensures this only runs on the initial login attempt (the re-dispatch
            // passes userType, so it can never loop).
            const otherThanAgency =
                userType == null &&
                availableTypes.length === 2 &&
                availableTypes.includes(UserType.Agency)
                    ? availableTypes.find(type => type !== UserType.Agency)
                    : undefined;
            if (otherThanAgency != null) {
                dispatch(login({ email, password, userType: otherThanAgency, isMobile }));
                return;
            }
            // make the user choose an available login type
            dispatch(authActions.setAvailableLoginTypes(data.data.user.availableUserTypes));
        } else {
            dispatch(authActions.failedLogin(null));
        }
    };
/**
 * Log back in after the session expired, as the same account and user type. Unlike `login` it never
 * clears `authenticatedUser`, so the page under `SessionExpiredDialog` stays mounted throughout.
 */
export const relogin =
    ({ password, isMobile }: { password: string; isMobile: boolean }): CommonThunk =>
    async (dispatch, getState) => {
        const current = getState().auth.authenticatedUser;
        if (current == null) return;

        dispatch(authActions.startRelogin());
        const [error, data] = await dispatch(
            callApi(
                apiClient.Auth.login({
                    email: current.email,
                    password,
                    userType: current.chosenUserType,
                }).request,
            ),
        );
        const user = data?.data?.user;
        if (error || user == null) {
            dispatch(authActions.failedRelogin(errorMessageFromResponse(error)));
            return;
        }

        user.chosenUserType = data?.data?.credentials?.chosen_user_type ?? current.chosenUserType;
        dispatch(finishLogin(user as unknown as User, isMobile));
    };

export const revalidateAccount =
    (key: string, isMobile: boolean): CommonThunk =>
    async dispatch => {
        dispatch(authActions.startRevalidation());

        const [error, data] = await dispatch(callApi(apiClient.Auth.revalidate(key).request));

        if (!error && data?.data?.user) {
            const { user, token } = data.data;

            if (data.data.credentials?.chosen_user_type != null && data?.data?.user != null) {
                data.data.user.chosenUserType = data.data.credentials.chosen_user_type;
                dispatch(finishLogin(data?.data?.user, isMobile));
            }

            dispatch(authActions.finishedRevalidation({ user, token }));
            dispatch(finishLogin(user, isMobile));
            return;
        }

        dispatch(authActions.failedRevalidation(errorMessageFromResponse(error)));
    };

export const finishLogin =
    (user: User, isMobile: boolean): CommonThunk =>
    async dispatch => {
        dispatch(
            uiThunks.setPreviewThemeOnLogin({
                skins: user?.skins as unknown as Skin[],
                userId: user?.id,
            }),
        );

        dispatch(normaliseDataThunks.updateWithNormalisedData(commonSchemas.User, user));
        dispatch(
            authActions.finishedLogin({
                user: user as unknown as User,
                isMobile,
            }),
        );
    };

export const validateUserSession =
    (isMobile: boolean): CommonThunk =>
    async dispatch => {
        dispatch(authActions.startValidatingSession());
        const [error, data] = await dispatch(callApi(apiClient.Entities.User.me().request));

        if (!error) {
            dispatch(authActions.finishedValidatingSession({ user: data.data as unknown as User }));
            dispatch(finishLogin(data?.data, isMobile));
            return;
        }
        dispatch(authActions.failedValidatingSession());
    };

export const resetLoginState = (): CommonThunk => async dispatch => {
    dispatch(authActions.resetLogin());
};

export const logoutLoggedInUser = (): CommonThunk => async dispatch => {
    dispatch(authActions.startLogout());
    await dispatch(callApi(apiClient.Auth.logout().request));

    dispatch(authActions.finishedLogout());
    dispatch(logout());
};

export const startImpersonatingUser =
    (userId: number, userType: UserType, history: History): CommonThunk =>
    async (dispatch, getState) => {
        if (getStartImpersonatingState(getState()).state === NetworkRequestState.InProgress) {
            return;
        }

        dispatch(authActions.startImpersonatingUser());

        const [error] = await dispatch(
            callApi(apiClient.Auth.startImpersonating(userId, userType).request),
        );

        if (error) {
            dispatch(authActions.failedImpersonatingUser(errorMessageFromResponse(error)));
            return;
        }

        storePreImpersonateSkin(currentSkin(getState()));
        dispatch(
            postImpersonation(true, history, errorUserMe =>
                dispatch(
                    authActions.failedImpersonatingUser(errorMessageFromResponse(errorUserMe)),
                ),
            ),
        );
        dispatch(authActions.finishedImpersonatingUser());
    };

export const stopImpersonatingUser =
    (history: History): CommonThunk =>
    async (dispatch, getState) => {
        if (getStopImpersonatingState(getState()).state === NetworkRequestState.InProgress) {
            return;
        }

        dispatch(authActions.startStopImpersonatingUser());

        const [error] = await dispatch(callApi(apiClient.Auth.stopImpersonating().request));

        if (error) {
            // don't care, just logout instead.
            dispatch(logoutLoggedInUser());
            return;
        }

        dispatch(postImpersonation(false, history, () => dispatch(logoutLoggedInUser())));
        dispatch(authActions.finishedStopImpersonatingUser());
    };

const setUserSkin =
    (user: APIUser): CommonThunk =>
    async dispatch => {
        if (user?.skins?.length > 0) {
            dispatch(uiThunks.setPreviewTheme(user?.skins?.[0] as unknown as Skin, true, user?.id));
        } else {
            dispatch(uiThunks.setPreviewTheme(null));
        }
    };

const postImpersonation =
    (started: boolean, history: History, onFailedUserMe: (error) => void): CommonThunk =>
    async dispatch => {
        const [errorUserMe, dataUserMe] = await dispatch(
            callApi(apiClient.Entities.User.me().request),
        );

        if (errorUserMe) {
            onFailedUserMe(errorUserMe);
            return;
        }

        let clonedUser = {};

        try {
            clonedUser = JSON.parse(JSON.stringify(dataUserMe?.data));
            // eslint-disable-next-line no-empty
        } catch (e) {}

        dispatch(
            authActions.finishedValidatingSession({ user: dataUserMe.data as unknown as User }),
        );

        dispatch(resetEntities());
        dispatch(normaliseDataThunks.updateWithNormalisedData(commonSchemas.User, dataUserMe.data));

        /**
         * Just doing a timeout delay to let the UI update and let users know the impersonation has been successful
         * before actually doing the switch over
         */
        setTimeout(() => {
            if (!started) {
                dispatch(uiThunks.setPreviewTheme(getPreImpersonateSkin()));
            } else dispatch(setUserSkin(clonedUser as APIUser));

            history.push(PostAuthRoutes.Dashboard.Dashboard);
            dispatch(authActions.resetStartImpersonatingUser());
            dispatch(authActions.resetStopImpersonatingUser());
        }, 1000);
    };
